<template>
  <section class="api-client">
    <!-- Page Header -->
    <div class="api-client__header">
      <div class="header-info">
        <div class="header-info__icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 18L22 12L16 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M8 6L2 12L8 18" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div>
          <h1 class="header-info__title">API Client</h1>
          <p class="header-info__subtitle">Manage your API keys, webhooks, and integrations</p>
        </div>
      </div>
      <div class="currency-switcher">
        <button
          v-for="curr in currencies"
          :key="curr.value"
          class="currency-switcher__btn"
          :class="{ 'currency-switcher__btn--active': selectedCurrency === curr.value }"
          @click="selectedCurrency = curr.value"
        >
          <span class="currency-switcher__flag">{{ curr.flag }}</span>
          {{ curr.value }}
        </button>
      </div>
    </div>

    <!-- Stats Cards -->
    <section class="stats-grid">
      <ApiClientCard
        title="Customers"
        :value="totalCustomers"
        valueType="Number"
      />
      <ApiClientCard
        title="Transaction Volume"
        :value="totalVolume"
        valueType="Money"
        :currency="selectedCurrency"
      />
    </section>

    <!-- Main Content Card -->
    <section class="content-card">
      <div class="content-card__header">
        <nav class="tabs-nav">
          <button
            class="tabs-nav__item"
            :class="{ 'tabs-nav__item--active': activeMenu === menu.value }"
            v-for="(menu, index) in apiMenu"
            :key="index"
            @click="activeMenu = menu.value"
          >
            <svg v-if="menu.value === 'Settings'" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9 11.25C10.2426 11.25 11.25 10.2426 11.25 9C11.25 7.75736 10.2426 6.75 9 6.75C7.75736 6.75 6.75 7.75736 6.75 9C6.75 10.2426 7.75736 11.25 9 11.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M14.55 11.25C14.4374 11.5012 14.4011 11.7774 14.4454 12.0472C14.4898 12.317 14.6128 12.5682 14.7975 12.7725L14.8425 12.8175C14.9919 12.9668 15.1101 13.1447 15.1904 13.3405C15.2707 13.5363 15.3116 13.7461 15.3116 13.9581C15.3116 14.1701 15.2707 14.3799 15.1904 14.5757C15.1101 14.7715 14.9919 14.9494 14.8425 15.0988C14.6932 15.2481 14.5153 15.3663 14.3195 15.4466C14.1237 15.527 13.9139 15.5678 13.7019 15.5678C13.4899 15.5678 13.2801 15.527 13.0843 15.4466C12.8885 15.3663 12.7106 15.2481 12.5612 15.0988L12.5162 15.0538C12.3119 14.869 12.0608 14.746 11.791 14.7017C11.5212 14.6573 11.245 14.6936 10.9938 14.8063C10.7479 14.9134 10.5394 15.0887 10.3919 15.3113C10.2444 15.5339 10.1637 15.7944 10.16 16.0613V16.1875C10.16 16.6149 9.98999 17.0248 9.68758 17.3273C9.38518 17.6297 8.97526 17.7997 8.54785 17.7997C8.12044 17.7997 7.71052 17.6297 7.40812 17.3273C7.10571 17.0248 6.93574 16.6149 6.93574 16.1875V16.1213C6.92661 15.8461 6.8367 15.5792 6.67747 15.3543C6.51823 15.1293 6.29672 14.9564 6.04012 14.8563C5.78893 14.7436 5.51273 14.7073 5.24294 14.7517C4.97315 14.796 4.72198 14.919 4.51762 15.1038L4.47262 15.1488C4.32327 15.2981 4.14538 15.4163 3.94959 15.4966C3.75379 15.577 3.54399 15.6178 3.33199 15.6178C3.11999 15.6178 2.91019 15.577 2.71439 15.4966C2.5186 15.4163 2.34071 15.2981 2.19137 15.1488C2.04203 14.9994 1.9238 14.8215 1.84347 14.6257C1.76315 14.4299 1.72228 14.2201 1.72228 14.0081C1.72228 13.7961 1.76315 13.5863 1.84347 13.3905C1.9238 13.1947 2.04203 13.0168 2.19137 12.8675L2.23637 12.8225C2.42117 12.6181 2.54418 12.367 2.58853 12.0972C2.63288 11.8274 2.59659 11.5512 2.48387 11.3C2.37678 11.0541 2.20146 10.8456 1.97888 10.6981C1.7563 10.5507 1.49582 10.4699 1.22887 10.4663H1.10262C0.675219 10.4663 0.265294 10.2963 -0.0371101 9.99389C-0.339514 9.69148 -0.509485 9.28156 -0.509485 8.85415C-0.509485 8.42674 -0.339514 8.01681 -0.0371101 7.71441C0.265294 7.412 0.675219 7.24203 1.10262 7.24203H1.16887C1.44407 7.2329 1.71097 7.14299 1.93594 6.98376C2.16091 6.82452 2.33377 6.60301 2.43387 6.34641C2.54659 6.09522 2.58288 5.81902 2.53853 5.54923C2.49418 5.27944 2.37117 5.02827 2.18637 4.82391L2.14137 4.77891C1.99203 4.62956 1.8738 4.45167 1.79347 4.25588C1.71315 4.06008 1.67228 3.85028 1.67228 3.63828C1.67228 3.42628 1.71315 3.21648 1.79347 3.02068C1.8738 2.82489 1.99203 2.647 2.14137 2.49766C2.29071 2.34832 2.4686 2.23009 2.66439 2.14976C2.86019 2.06944 3.06999 2.02857 3.28199 2.02857C3.49399 2.02857 3.70379 2.06944 3.89959 2.14976C4.09538 2.23009 4.27327 2.34832 4.42262 2.49766L4.46762 2.54266C4.67198 2.72746 4.92315 2.85047 5.19294 2.89482C5.46273 2.93917 5.73893 2.90288 5.99012 2.79016H6.04012C6.28603 2.68307 6.49447 2.50775 6.64195 2.28517C6.78943 2.06259 6.87019 1.80211 6.87387 1.53516V1.40891C6.87387 0.981501 7.04384 0.571576 7.34625 0.269172C7.64865 -0.0332313 8.05858 -0.203203 8.48598 -0.203203C8.91339 -0.203203 9.32332 -0.0332313 9.62572 0.269172C9.92812 0.571576 10.0981 0.981501 10.0981 1.40891V1.47516C10.1018 1.74211 10.1825 2.00259 10.33 2.22517C10.4775 2.44775 10.6859 2.62307 10.9319 2.73016C11.183 2.84288 11.4592 2.87917 11.729 2.83482C11.9988 2.79047 12.25 2.66746 12.4544 2.48266L12.4994 2.43766C12.6487 2.28832 12.8266 2.17009 13.0224 2.08976C13.2182 2.00944 13.428 1.96857 13.64 1.96857C13.852 1.96857 14.0618 2.00944 14.2576 2.08976C14.4534 2.17009 14.6313 2.28832 14.7806 2.43766C14.93 2.587 15.0482 2.76489 15.1285 2.96068C15.2088 3.15648 15.2497 3.36628 15.2497 3.57828C15.2497 3.79028 15.2088 4.00008 15.1285 4.19588C15.0482 4.39167 14.93 4.56956 14.7806 4.71891L14.7356 4.76391C14.5508 4.96827 14.4278 5.21944 14.3835 5.48923C14.3391 5.75902 14.3754 6.03522 14.4881 6.28641V6.33641C14.5952 6.58232 14.7706 6.79076 14.9931 6.93824C15.2157 7.08572 15.4762 7.16648 15.7431 7.17016H15.8694C16.2968 7.17016 16.7067 7.34013 17.0091 7.64254C17.3115 7.94494 17.4815 8.35487 17.4815 8.78227C17.4815 9.20968 17.3115 9.6196 17.0091 9.92201C16.7067 10.2244 16.2968 10.3944 15.8694 10.3944H15.8031C15.5362 10.398 15.2757 10.4788 15.0531 10.6263C14.8306 10.7737 14.6552 10.9822 14.5481 11.2281V11.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else-if="menu.value === 'Customers'" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.75 15.75V14.25C12.75 13.4544 12.4339 12.6913 11.8713 12.1287C11.3087 11.5661 10.5456 11.25 9.75 11.25H3.75C2.95435 11.25 2.19129 11.5661 1.62868 12.1287C1.06607 12.6913 0.75 13.4544 0.75 14.25V15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6.75 8.25C8.40685 8.25 9.75 6.90685 9.75 5.25C9.75 3.59315 8.40685 2.25 6.75 2.25C5.09315 2.25 3.75 3.59315 3.75 5.25C3.75 6.90685 5.09315 8.25 6.75 8.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M17.25 15.75V14.25C17.2494 13.5853 17.0287 12.9396 16.6213 12.4142C16.2139 11.8889 15.6436 11.5137 15 11.3475" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 2.34753C12.6453 2.51276 13.2173 2.88808 13.6257 3.41427C14.0342 3.94047 14.2551 4.58778 14.2551 5.25378C14.2551 5.91978 14.0342 6.56709 13.6257 7.09328C13.2173 7.61948 12.6453 7.9948 12 8.16003" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else-if="menu.value === 'Members'" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 15.75V14.25C15 13.4544 14.6839 12.6913 14.1213 12.1287C13.5587 11.5661 12.7956 11.25 12 11.25H6C5.20435 11.25 4.44129 11.5661 3.87868 12.1287C3.31607 12.6913 3 13.4544 3 14.25V15.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M9 8.25C10.6569 8.25 12 6.90685 12 5.25C12 3.59315 10.6569 2.25 9 2.25C7.34315 2.25 6 3.59315 6 5.25C6 6.90685 7.34315 8.25 9 8.25Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <svg v-else-if="menu.value === 'Transactions'" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M1.5 13.5L6 9L9.75 12.75L16.5 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12 6H16.5V10.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            {{ menu.name }}
          </button>
        </nav>

        <div
          class="env-toggle"
          :class="{ 'env-toggle--disabled': !isProduction }"
        >
          <span class="env-toggle__label" :class="{ 'env-toggle__label--active': !live }">Test Mode</span>
          <ToggleButton
            @value-selected="checkToggle"
            :reset="resetToggle"
            :value="live"
            :disabled="!isProduction"
            v-can.disable="'api:update'"
          />
          <span class="env-toggle__label" :class="{ 'env-toggle__label--active': live }">Live Mode</span>
        </div>
      </div>

      <section class="content-card__body">
        <KeepAlive>
          <component :is="activeMenu" />
        </KeepAlive>
      </section>
    </section>
  </section>
</template>

<script>
  import ApiClientCard from "@/components/apiClient/ApiClientCard";
import Customers from "@/components/apiClient/Customers";
import Members from "@/components/apiClient/Members";
import Settings from "@/components/apiClient/Settings";
import Transactions from "@/components/apiClient/Transactions";
import ToggleButton from "@/components/general/ToggleButton";
import { getItem } from "@/utils/storage.js";
import { mapActions, mapGetters } from "vuex";
  export default {
    name: "ApiClient",
    components: {
      ApiClientCard,
      ToggleButton,
      Settings,
      Customers,
      Members,
      Transactions,
    },
    data() {
      return {
        apiMenu: [
          {
            name: "API Settings",
            value: "Settings",
          },
          {
            name: "Customers ",
            value: "Customers",
          },
          {
            name: "Members ",
            value: "Members",
          },
          {
            name: "Transactions",
            value: "Transactions",
          },
        ],
        resetToggle: false,
        activeMenu: "Settings",
        currencies: [
          { value: "USD", flag: "🇺🇸", name: "US Dollar" },
          { value: "NGN", flag: "🇳🇬", name: "Nigerian Naira" },
          { value: "KES", flag: "🇰🇪", name: "Kenyan Shilling" },
        ],
      };
    },
    computed: {
      ...mapGetters("organisationModule", ["organisationDetails"]),
      ...mapGetters("ApiClient", ["totalCustomers", "totalTransactionVolume"]),
      selectedCurrency: {
        get() {
          return this.$store.getters["ApiClient/currency"];
        },
        set(value) {
          this.$store.commit("ApiClient/SET_CURRENCY", value);
          this.getCustomers();
        },
      },
      totalVolume() {
        if (!this.totalTransactionVolume || this.totalTransactionVolume.length === 0) {
          return 0;
        }
        // Find volume for selected currency
        const currencyVolume = this.totalTransactionVolume.find(
          (vol) => vol.currency === this.selectedCurrency
        );
        return currencyVolume ? currencyVolume.totalVolume : 0;
      },
      envModelabel() {
        let label = null;
        switch (this.live) {
          case true:
            label = "Switch to Test Mode";
            break;
          case false:
            label = "Switch to Live Mode";
            break;
        }
        return label;
      },
      live() {
        // Always show the actual state, even in non-production
        // (toggling is disabled via :disabled prop)
        return this.organisationDetails.live;
      },
      isProduction() {
        console.log({NODE_ENV: process.env.NODE_ENV})
        return process.env.NODE_ENV === 'production';
      },
    },
    created() {
      this.fetchInfo();
    },

    methods: {
      ...mapActions("ApiClient", [
        "getCustomers",
        "toggleEnvironment",
        "getTotalTransactionVolume",
        "getApikey",
        "getApiWebHook"
      ]),
      ...mapActions("organisationModule", ["getOrganisationProfile"]),
      ...mapActions(["showToast"]),
      async fetchInfo() {
        const orgInfo = getItem("org_details");
        const orgId = orgInfo._id;
        try {
          await Promise.all([
            this.getCustomers(),
            this.getOrganisationProfile(orgId),
            this.getTotalTransactionVolume(),
            this.getApikey(),
            this.getApiWebHook()
          ]);
        } catch (error) {
          return error;
        }
      },
      checkToggle(val) {
        if (val !== this.live) {
          this.toggleLiveMode(val);
        }
      },
      async toggleLiveMode(value) {
        // Prevent enabling live mode in non-production environments
        if (!this.isProduction && value === true) {
          this.showToast({
            description: "Live mode can only be enabled in production environment",
            display: true,
            type: "error",
          });
          this.resetToggle = true;
          setTimeout(() => {
            this.resetToggle = false;
          }, 2000);
          return;
        }

        try {
          const response = await this.toggleEnvironment({
            live: value,
          });
          const { message } = response.data;
          this.showToast({
            description: message,
            display: true,
            type: "success",
          });
          // Always reload webhooks section after toggle to ensure proper state
          await this.fetchInfo();
        } catch (error) {
          setTimeout(() => {
            this.resetToggle = false;
          }, 2000);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .api-client {
    padding-bottom: 40px;
    
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      flex-wrap: wrap;
      gap: 20px;
      margin-bottom: 24px;
    }
  }
  
  .header-info {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    
    &__icon {
      width: 48px;
      height: 48px;
      background: linear-gradient(135deg, #4D84FF 0%, #6B9AFF 100%);
      border-radius: 12px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #ffffff;
      flex-shrink: 0;
    }
    
    &__title {
      font-family: 'DM Sans', sans-serif;
      font-size: 24px;
      font-weight: 500;
      color: #0F172A;
      margin: 0 0 4px 0;
    }
    
    &__subtitle {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #64748B;
      margin: 0;
    }
  }
  
  .currency-switcher {
    display: flex;
    align-items: center;
    background: #F1F5F9;
    border-radius: 10px;
    padding: 4px;
    gap: 4px;
    
    &__btn {
      display: flex;
      align-items: center;
      gap: 6px;
      padding: 10px 16px;
      background: transparent;
      border: none;
      border-radius: 8px;
      font-family: 'DM Sans', sans-serif;
      font-size: 13px;
      font-weight: 500;
      color: #64748B;
      cursor: pointer;
      transition: all 0.2s ease;
      white-space: nowrap;
      
      &:hover {
        color: #0F172A;
        background: rgba(255, 255, 255, 0.5);
      }
      
      &--active {
        background: #ffffff;
        color: #0F172A;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
      }
    }
    
    &__flag {
      font-size: 16px;
      line-height: 1;
    }
  }
  
  .stats-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 20px;
    margin-bottom: 24px;
  }
  
  .content-card {
    background: #ffffff;
    border-radius: 12px;
    border: 1px solid #E2E8F0;
    overflow: hidden;
    
    &__header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 20px 24px;
      border-bottom: 1px solid #E2E8F0;
      gap: 20px;
      flex-wrap: wrap;
    }
    
    &__body {
      padding: 0;
    }
  }
  
  .tabs-nav {
    display: flex;
    align-items: center;
    gap: 8px;
    overflow-x: auto;
    scrollbar-width: none;
    
    &::-webkit-scrollbar {
      display: none;
    }
    
    &__item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 10px 16px;
      background: transparent;
      border: none;
      border-radius: 8px;
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 400;
      color: #64748B;
      cursor: pointer;
      transition: all 0.2s ease;
      white-space: nowrap;
      
      svg {
        flex-shrink: 0;
      }
      
      &:hover {
        background: #F8FAFC;
        color: #0F172A;
      }
      
      &--active {
        background: #EEF4FF;
        color: #4D84FF;
        font-weight: 500;
        
        svg {
          color: #4D84FF;
        }
      }
    }
  }
  
  .env-toggle {
    display: flex;
    align-items: center;
    gap: 12px;
    
    &--disabled {
      opacity: 0.5;
      filter: blur(1px);
      pointer-events: none;
    }
    
    &__label {
      font-family: 'DM Sans', sans-serif;
      font-size: 14px;
      font-weight: 500;
      color: #64748B;
      transition: color 0.2s ease;
      
      &--active {
        color: #0F172A;
        font-weight: 600;
      }
    }
  }
  
  @media (max-width: 640px) {
    .api-client__header {
      flex-direction: column;
      align-items: stretch;
    }
    
    .content-card__header {
      flex-direction: column;
      align-items: stretch;
    }
    
    .tabs-nav {
      order: 2;
    }
    
    .env-toggle {
      order: 1;
      justify-content: flex-end;
    }
  }
</style>
